import React, { forwardRef, useCallback, useMemo } from 'react';

import { useComponentVisible } from 'hooks/visibility';
import {
  FieldStructure,
  FieldStructureProps,
} from '../../../structures/FieldStructure';
import { OptionsBlockProps, ScSelectMainProps, SelectOption } from '../types';
import { ProfileSelectButton } from '../components/ProfileSelectButton';
import { ProfileOptionsBlock } from '../components/ProfileOptionsBlock';

import { ScSelectBlock } from './styled';

type SelectButtonProps = FieldStructureProps &
  ScSelectMainProps & {
    selectedOption?: SelectOption;
    selected?: SelectOption['value'];
    defaultValue?: React.ReactNode;
    active?: boolean;
    handleOpen?: () => void;
    handleReset?: (e: any) => void;
    placeholder?: string;
    canReset?: boolean;
    selectedOptionWithoutIcon?: boolean;
  };

export type SelectBlockProps = SelectButtonProps & {
  options: SelectOption[];
  onSelect?: (value: SelectOption['value']) => void;
  onOpen?: () => void;
  onClose?: () => void;
  native?: boolean;
  isFilter?: boolean;
} & Pick<OptionsBlockProps, 'minOptionHeight'>;

export const ProfileSelectBlock: (
  props: SelectBlockProps & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null = forwardRef(
  (
    {
      placeholder,
      options,
      selected,
      error,
      hideErrorMessage,
      onSelect,
      disabled,
      defaultValue,
      onOpen,
      onClose,
      native,
      canReset,
      ...scProps
    }: SelectBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const {
      ref,
      openerRef,
      isComponentVisible,
      setIsComponentVisible,
    } = useComponentVisible(false);

    const handleOpen = () => {
      if (!!disabled) return;

      setIsComponentVisible(!isComponentVisible);
    };

    const selectedOption = useMemo(
      () =>
        typeof selected === 'undefined'
          ? undefined
          : options.find(({ value }) => value === selected),
      [selected, options]
    );

    const handleReset = useCallback(
      (e: any) => {
        e.stopPropagation();
        onSelect && onSelect('');
        isComponentVisible && setIsComponentVisible(false);
      },
      [isComponentVisible, onSelect, setIsComponentVisible]
    );

    return (
      <FieldStructure error={error} hideErrorMessage={hideErrorMessage}>
        <ScSelectBlock ref={forwardedRef} active={isComponentVisible}>
          <ProfileSelectButton
            ref={openerRef}
            {...scProps}
            disabled={disabled}
            error={error}
            active={isComponentVisible}
            selectedOption={selectedOption}
            placeholder={placeholder}
            handleOpen={handleOpen}
            handleReset={handleReset}
            selected={selected}
            canReset={canReset}
          />
          {isComponentVisible && (
            <ProfileOptionsBlock
              ref={ref}
              {...scProps}
              selectedOption={selectedOption}
              options={options}
              onChange={(value) => {
                onSelect && onSelect(value);
                handleOpen();
              }}
            />
          )}
        </ScSelectBlock>
      </FieldStructure>
    );
  }
);
